import React from 'react';
import type { Step } from 'react-joyride';

function TourBody({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rc-tour__body">
      <h3 className="rc-tour__title">{title}</h3>
      <p className="rc-tour__text">{children}</p>
    </div>
  );
}

const base: Partial<Step> = {
  placement: 'right',
};

/**
 * Job seeker tour — also used when a working professional switches to
 * "Want a Referral" mode (nav slugs are identical in both variants).
 */
export const JOBSEEKER_TOUR_STEPS: Step[] = [
  // Welcome
  {
    ...base,
    target: 'body',
    placement: 'center',
    content: (
      <TourBody title="Welcome to ReffConnect">
        Your referral-powered job search starts here. Let us show you around it takes about
        30 seconds.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-jobseeker-dashboard"]',
    content: (
      <TourBody title="Dashboard">
        A live snapshot of your job search profile strength, recent activity, and quick
        actions to get you moving.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-resume-grader"]',
    content: (
      <TourBody title="AI Resume Grader">
        Get instant AI feedback on your resume. It highlights gaps, scores your content, and
        suggests improvements before you apply.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-group-services"]',
    content: (
      <TourBody title="Expert Services">
        Book 1-on-1 sessions with verified professionals resume reviews, mock interviews,
        and referral calls with people at your target companies.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-my-applications"]',
    content: (
      <TourBody title="My Applications">
        Track every application you have submitted see status updates, referral progress, and
        follow-up reminders in one place.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-jobopenings"]',
    content: (
      <TourBody title="Job Openings">
        Browse roles where insiders are ready to refer you and apply with a real advantage
        over cold applications.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-my-sessions"]',
    content: (
      <TourBody title="My Sessions">
        Every booking upcoming and completed tracked in one place. Join calls, view
        feedback, and manage your schedule.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-referral-drops"]',
    content: (
      <TourBody title="Weekly Drops">
        Fresh referral slots drop every week from verified professionals at top companies.
        Grab them before they fill up.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-invoices"]',
    content: (
      <TourBody title="Invoices">
        All your payment receipts and transaction history download invoices for any booking.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-profile"]',
    content: (
      <TourBody title="My Profile">
        Your professional identity update your resume, skills, preferences, and banner.
        A strong profile increases referral success.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="topbar-search"]',
    placement: 'bottom',
    content: (
      <TourBody title="Quick Search">
        Find mentors, companies, services, and openings instantly from anywhere on your
        dashboard. Use keyboard shortcuts for speed.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="hired-button"]',
    placement: 'bottom',
    content: (
      <TourBody title="Got Hired?">
        Land the job? Tell us here you will unlock the Working Professional dashboard and can
        start referring (and earning) yourself.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="tour-replay"]',
    placement: 'bottom',
    content: (
      <TourBody title="That's the tour!">
        Replay this guide anytime from this compass icon. Good luck out there your next role
        is one referral away.
      </TourBody>
    ),
  },
];

/** Working professional (referrer) tour. */
export const EMPLOYEE_TOUR_STEPS: Step[] = [
  // Welcome
  {
    ...base,
    target: 'body',
    placement: 'center',
    content: (
      <TourBody title="Welcome to your Professional Workspace">
        Refer great candidates, offer expert services, and earn here is a quick tour of how
        it all works.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-referral-dashboard"]',
    content: (
      <TourBody title="Dashboard">
        Earnings, upcoming sessions, and referral activity your whole practice at a glance
        with real-time stats.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-services"]',
    content: (
      <TourBody title="My Services">
        Configure what you offer resume reviews, mock interviews, referral sessions. Set your
        price, duration, and weekly availability.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-post-job"]',
    content: (
      <TourBody title="Post a Job">
        Share openings from your company and attract candidates you can refer. Each posting
        shows up in the job seeker marketplace.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-payouts"]',
    content: (
      <TourBody title="Payouts">
        Track what you have earned and withdraw whenever you like. View pending, completed, and
        upcoming payouts.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-referral-drops"]',
    content: (
      <TourBody title="Referral Drops">
        Manage your weekly referral drop slots. Job seekers compete for your limited referral
        slots each week.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-invoices"]',
    content: (
      <TourBody title="Invoices">
        All payment records and earnings receipts download or review your transaction history.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-my-sessions"]',
    content: (
      <TourBody title="My Sessions">
        Your booked sessions with candidates upcoming and past all in one place. Accept
        requests, schedule calls, and deliver feedback.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="nav-profile"]',
    content: (
      <TourBody title="My Profile">
        Your public-facing profile that job seekers see. Keep it updated a strong profile
        attracts more bookings.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="topbar-search"]',
    placement: 'bottom',
    content: (
      <TourBody title="Quick Search">
        Search across your sessions, candidates, and requests instantly from the header.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="mode-switcher"]',
    content: (
      <TourBody title="Role Switcher">
        Job-hunting yourself? Switch to "Want a Referral" anytime you get the full job seeker
        toolkit too. Toggle back whenever you want.
      </TourBody>
    ),
  },
  {
    ...base,
    target: '[data-tour="tour-replay"]',
    placement: 'bottom',
    content: (
      <TourBody title="That's the tour!">
        Replay this guide anytime from this compass icon. Happy referring and earning!
      </TourBody>
    ),
  },
];
